import React, { useEffect } from 'react';
import '../../App.css';
import '../../tools/search-results/search-results.css';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// images

// classes
import SearchCatalogResultsCaom from './search-catalog-results-caom';
import SearchCatalogFormCaom from './search-catalog-form-caom';
import { TabBar, TabButton } from '../../tools/tab-component/tab-component';
import { useTranslation } from 'react-i18next';
import { ContextMenu } from '../menus/context-menu';

// functions
import { ContainerHook } from '../../utils/functions';

// types
import { ComputeServiceType, DataItemType, JobType, Site } from '../../utils/types';

// enum

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import {
  addSearchResults,
  setContextMenuRefreshAddMetadata,
  setSearchCatalogTab,
} from './search-catalog-caom-slice';
import {
  buildQuery,
  getDataProductTypesInResults,
  getTapData,
} from './search-catalog-caom-actions';
import {
  CONTEXT_SYSTEMS,
  CONTEXT_CONTAINERS,
  ctx_setContextRow,
  ctx_setContextVisible,
} from '../menus/context-menu-slice';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

// types specifically for this component.
import {
  SelectedRowsType,
  QueryParameters,
} from '../search-catalog/search-catalog-types';

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function SearchCatalogCaom(props: { dataManagementJobs: JobType[] }) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // GLOBAL STATE VARIABLES:
  const sG_project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );

  // DATA STATE VARIABLES:
  const sD_computeServices: ComputeServiceType[] = useAppSelector(
    (state) => state.data.computeServices,
  );

  // LOCAL STATE VARIABLES:

  // maintain the state for the selected rows on the results tabs.
  const sSelectedRows: SelectedRowsType[] = useAppSelector(
    (state) => state.searchCatalog.selectedItems.selectedRows,
  );

  // search catalog tool:
  const sSearchCatalogTab: string = useAppSelector(
    (state) => state.searchCatalog.searchCatalogTab,
  );
  const sSearchResults: QueryParameters = useAppSelector(
    (state) => state.searchCatalog.searchResults,
  );
  const sDPTRetrieved: boolean = useAppSelector(
    (state) => state.searchCatalog.dptRetrieved,
  );
  const sDPTFilter: string = useAppSelector((state) => state.searchCatalog.dptFilter);
  const sPage: number = useAppSelector((state) => state.searchCatalog.page);
  const sPageSize: number = useAppSelector((state) => state.searchCatalog.pageSize);
  const sDisplayedPage: { dptFilter: string; page: number; pageSize: number } =
    useAppSelector((state) => state.searchCatalog.displayedPage);
  const sContextMenuRefreshAddMetadata: number = useAppSelector(
    (state) => state.searchCatalog.contextMenus.refreshAddMetadata,
  );

  //	-------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	-------------------------------------------------

  // translation function
  const { t } = useTranslation();

  // monitor this container, and if it changes size update the state.
  const dataWindow = ContainerHook({
    container: CONTEXT_CONTAINERS.SEARCH_CATALOG_WHOLE_WINDOW,
  });

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	-------------------------------------------------
  //
  //	function that adds a new search results tab when the user clicks on
  //	search on the search parameters form.
  //
  //	-------------------------------------------------

  function addSearchResultsTab(args: { query: QueryParameters }) {
    //
    // replace the current result set with the new query.
    dispatch(addSearchResults(args.query));

    // build the ADQL representation of the query.
    dispatch(buildQuery({ query: args.query }));

    // switch to the results tab.
    dispatch(setSearchCatalogTab('results'));

    // kick off the search.
    dispatch(getTapData({ query: args.query }));

    // get a list of data-product types in this search.
    dispatch(getDataProductTypesInResults({ query: args.query }));
    //
  } // addSearchResultsTab

  //	------------------------------------------------------------
  //
  //	Handler for button onClick events.
  //
  //	------------------------------------------------------------

  const onClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    const button: HTMLButtonElement = event.currentTarget;

    // set which tab is selected on the Search Catalog form. If we clicked on a results tab, then store the index of the tab.
    if (button.name === 'btnSelectedItems') {
      dispatch(setSearchCatalogTab('selected-items'));
    }

    // if we clicked on the results tab, switch to it.
    if (button.name === 'btnResults') {
      dispatch(setSearchCatalogTab('results'));
    }
    //
  }; // onClickHandler

  //	------------------------------------------------------------
  //
  //	close the context menus when the user clicks anywhere
  //	in the container.
  //
  //	------------------------------------------------------------

  function onClickDivHandler(event: React.MouseEvent<HTMLDivElement>) {
    //
    dispatch(
      ctx_setContextRow({
        contextSystem: CONTEXT_SYSTEMS.SEARCH_CATALOG_FORM,
        row: -1,
      }),
    );
    dispatch(
      ctx_setContextVisible({
        contextSystem: CONTEXT_SYSTEMS.SEARCH_CATALOG_FORM,
        visible: false,
      }),
    );
    dispatch(setContextMenuRefreshAddMetadata(Date.now()));
    //
  } // onClickDivHandler

  //	-------------------------------------------------
  //
  //	E V E N T S
  //
  //	-------------------------------------------------

  //	--------------------------------------------------------------------------
  //
  //	H O O K S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	re-run the search-catalog query if the user has changed
  //	the data-product type filter.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    let reRunQuery: boolean = false;
    if (sDisplayedPage !== undefined) {
      //
      // has the filter, page, or page size on the current tab changed?
      reRunQuery =
        reRunQuery ||
        sDisplayedPage.dptFilter !== sDPTFilter ||
        sDisplayedPage.page !== sPage ||
        sDisplayedPage.pageSize !== sPageSize;
      //
    }

    // re-run the query.
    if (reRunQuery === true && sDPTRetrieved) {
      dispatch(getTapData({ query: sSearchResults }));
    }
    //
  }, [sDPTFilter, sPage, sPageSize]);

  //	-------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	-------------------------------------------------

  return (
    <div
      ref={dataWindow}
      id="search-catalog-whole-window"
      style={{
        height: '100%',
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'row',
        padding: '0px 5px 0px 0px',
        position: 'relative',
      }}
      onClick={onClickDivHandler}
    >
      <div className="left-panel br-d-r">
        {/*
        ///  the new-search panel
        */}
        <SearchCatalogFormCaom
          addSearchResultsTab={addSearchResultsTab}
          project={sG_project}
        />
      </div>
      <div
        style={{
          /*flex: '1 1 auto',*/
          display: 'flex',
          flexDirection: 'column',
          /*minWidth: '0',*/
          flex: '1 1',
          height: '100%',
          width: '0',
          padding: '0px 10px 0px 10px',
        }}
      >
        {/*
        ///  the query results panel
        */}
        <div
          role="region"
          aria-label="Search results"
          className="panel-main" // flex-grow-1 min-h-0 d-flex flex-column"
          style={{
            flex: '3 3',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            zIndex: 0,
          }}
        >
          {/*
          ///  header row containing Results tab and Selected items tab
          */}
          <TabBar aria-label={t('Search results tabs')} style={{ flex: '0 0 auto' }}>
            <div style={{ flex: '0 0 8px' }} />
            <TabButton
              index={0}
              text={t('Results')}
              onClick={() => {
                if (!sDPTRetrieved) {
                  return;
                }
                dispatch(setSearchCatalogTab('results'));
              }}
              onClose={undefined}
              selected={sDPTRetrieved && sSearchCatalogTab === 'results'}
              disabled={!sDPTRetrieved}
            />
            <div style={{ flex: '0 0 8px' }} />
          </TabBar>

          {/*
          ///  display the single results
          */}
          <div
            className="flex-grow-1"
            style={{
              minHeight: 0,
            }}
          >
            {sDPTRetrieved && (
              <SearchCatalogResultsCaom
                key={'results'}
                query={sSearchResults}
                hidden={sSearchCatalogTab !== 'results'}
              />
            )}
          </div>
        </div>
      </div>
      <ContextMenu
        key={'form_' + sContextMenuRefreshAddMetadata.toString()}
        system={CONTEXT_SYSTEMS.SEARCH_CATALOG_FORM}
      />
    </div>
  );
  //
} // SearchCatalogCaom
